import { computed } from '@preact/signals-react'
import type { RoomState } from '../../../room/room.types'
import type { GameOptionsDialogDep } from './gameOptionsDialog.types'
import type { GameRegistry } from '../../../catalog/gameRegistry/gameRegistry.types'

function getPlayerCountBounds(gameId: string, gameRegistry: GameRegistry): { minPlayers: number; maxPlayers: number } {
  const meta = gameRegistry.getGameMetadata?.(gameId)
  const minPlayers = meta?.minPlayers ?? 2
  const maxPlayers = meta?.maxPlayers ?? minPlayers
  return { minPlayers, maxPlayers }
}

function computeSlotCountFromActiveGame(activeGame: any): number {
  if (!activeGame) return 2
  const ctxNumPlayers = Number(activeGame?.ctx?.numPlayers)
  if (Number.isFinite(ctxNumPlayers) && ctxNumPlayers >= 2) return ctxNumPlayers
  if (Array.isArray(activeGame?.playerToMateIds) && activeGame.playerToMateIds.length >= 2) {
    return activeGame.playerToMateIds.length
  }
  return 2
}

export function makeGameOptionsDialogDep(roomState: RoomState, gameRegistry: GameRegistry): GameOptionsDialogDep {
  const activeSlotCount = computed(() => computeSlotCountFromActiveGame(roomState.activeGame.value))

  const availablePlayerCounts = computed(() => {
    const gameId = roomState.activeGame.value?.gameId
    if (!gameId) return [2]
    const { minPlayers, maxPlayers } = getPlayerCountBounds(gameId, gameRegistry)
    const counts: number[] = []
    for (let n = minPlayers; n <= maxPlayers; n++) counts.push(n)
    return counts.length > 0 ? counts : [2]
  })

  const defaultPlayerCount = computed(() => {
    const counts = availablePlayerCounts.value
    const slotCount = activeSlotCount.value
    if (counts.includes(slotCount)) return slotCount
    return counts[counts.length - 1] || 2
  })

  function getAvailablePlayerCountsForGame(gameId: string): number[] {
    const { minPlayers, maxPlayers } = getPlayerCountBounds(gameId, gameRegistry)
    const counts: number[] = []
    for (let n = minPlayers; n <= maxPlayers; n++) counts.push(n)
    return counts.length > 0 ? counts : [2]
  }

  function getDefaultPlayerCountForGame(gameId: string, mateCount: number): number {
    const { minPlayers, maxPlayers } = getPlayerCountBounds(gameId, gameRegistry)
    if (mateCount < minPlayers) return minPlayers
    return Math.min(maxPlayers, mateCount)
  }

  return {
    activeSlotCount,
    availablePlayerCounts,
    defaultPlayerCount,
    getAvailablePlayerCountsForGame,
    getDefaultPlayerCountForGame,
  }
}

